import { PROSPECT_STAGE_RANK, isDeadEnd, prospectStageRank } from './prospects';
import type { Prospect, ProspectStatus } from './prospects';

/**
 * The chips over the prospect list, and the funnel rows above them.
 *
 * Two different questions, so two controls (drop A, 17 September):
 *
 * - A chip is "who is sitting HERE right now". Working (everything still
 *   moving, not yet promoted) is the default: it is the list Hanneza
 *   actually chases from. Promoted rows keep their status but leave the
 *   working view, because their work continues on the lead. Dead ends are
 *   one chip, not two — the question is "who did we give up on", not which
 *   way.
 * - A funnel row is "who REACHED this stage or went past it". Those are the
 *   `reached_*` filters; clicking a row shows exactly the people its number
 *   counts. See prospectFunnel.ts.
 */
export type ProspectFilter =
  | 'working'
  | 'new'
  | 'pending'
  | 'connected'
  | 'pitched'
  | 'dm_2'
  | 'dm_3'
  | 'dead'
  | 'left_company'
  | 'cant_connect'
  | 'promoted'
  | 'all'
  | 'reached_pending'
  | 'reached_connected'
  | 'reached_pitched'
  | 'reached_dm_2'
  | 'reached_dm_3';

export const PROSPECT_FILTERS: { key: ProspectFilter; label: string }[] = [
  { key: 'working', label: 'Working' },
  { key: 'new', label: 'New' },
  { key: 'pending', label: 'Request sent' },
  { key: 'connected', label: 'Connected' },
  { key: 'pitched', label: '1st DM' },
  { key: 'dm_2', label: '2nd DM' },
  { key: 'dm_3', label: '3rd DM' },
  { key: 'dead', label: 'Dead ends' },
  { key: 'promoted', label: 'Promoted' },
  { key: 'all', label: 'All' },
];

const REACHED: Partial<Record<ProspectFilter, ProspectStatus>> = {
  reached_pending: 'pending',
  reached_connected: 'connected',
  reached_pitched: 'pitched',
  reached_dm_2: 'dm_2',
  reached_dm_3: 'dm_3',
};

/** Reached this stage or went past it. Dead ends never did; promoted keeps its rank. */
export function reachedStage(prospect: Pick<Prospect, 'status'>, stage: ProspectStatus): boolean {
  const rank = prospectStageRank(prospect.status);
  const floor = PROSPECT_STAGE_RANK[stage];
  return rank !== null && floor !== null && rank >= floor;
}

export function matchesProspectFilter(prospect: Prospect, filter: ProspectFilter): boolean {
  const promoted = prospect.promoted_at !== null;
  const reached = REACHED[filter];
  if (reached !== undefined) return reachedStage(prospect, reached);
  switch (filter) {
    case 'all':
      return true;
    case 'promoted':
      return promoted;
    case 'dead':
      return !promoted && isDeadEnd(prospect.status);
    case 'working':
      return !promoted && !isDeadEnd(prospect.status);
    default:
      return !promoted && prospect.status === filter;
  }
}

export function filterProspects(
  prospects: readonly Prospect[],
  filter: ProspectFilter,
): Prospect[] {
  return prospects.filter((prospect) => matchesProspectFilter(prospect, filter));
}

export type ProspectTierFilter = 'all' | 'tier1' | 'tier2' | 'trash' | 'untagged';

/** All tiers includes untagged rows; only Show Trash exposes trash. */
export function filterProspectsByTier(
  prospects: readonly Prospect[],
  tier: ProspectTierFilter,
): Prospect[] {
  return prospects.filter((prospect) =>
    tier === 'all'
      ? prospect.tier !== 'trash'
      : tier === 'untagged'
        ? prospect.tier === null
        : prospect.tier === tier,
  );
}

/**
 * Newest first, by the day they were FOUND.
 *
 * `created_at` is when the Hub heard of somebody, which is the same thing
 * only while people are typing them in one at a time. An import brings weeks
 * across in one pass, and ordering those by created_at puts them all on the
 * same second in whatever order the insert happened to run. `sourced_on`
 * carries the sheet's own date; a prospect added by hand has none, and falls
 * back to the day the row was written, which for that prospect is the same
 * fact. See 20270123000100.
 */
export function foundOn(prospect: Pick<Prospect, 'sourced_on' | 'created_at'>): string {
  return prospect.sourced_on ?? prospect.created_at.slice(0, 10);
}

export function sortProspects(prospects: readonly Prospect[]): Prospect[] {
  return [...prospects].sort(
    (a, b) => foundOn(b).localeCompare(foundOn(a)) || b.created_at.localeCompare(a.created_at),
  );
}

export function searchProspects(prospects: readonly Prospect[], query: string): Prospect[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...prospects];
  return prospects.filter((p) =>
    `${p.first_name} ${p.last_name} ${p.company_name} ${p.title} ${p.email}`
      .toLowerCase()
      .includes(needle),
  );
}
